import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useState } from 'react';

import { TRAFFIC_TYPES } from '@/api/common';
import type { OfflineActivityDataType } from '@/api/offlineEvents';
import {
  InstantSearchDataTable,
  type InstantSearchDataTableColumnDef,
} from '@/components/InstantSearch';
import { usePageFilterContext } from '@/components/page';
import { Panel } from '@/components/panels';
import { Link } from '@/components/typography';
import type SolAPIError from '@/error/SolAPIError';
import DataTableAdvancedFiltersDialog from '@/features/AdvancedFiltersModal/DataTableAdvancedFiltersDialog';
import { DataTableMetricSelector } from '@/features/DataTableMetricSelector';
import { VisitActivityLegend } from '@/features/VisitActivityLegend';
import { ROUTES } from '@/router';

import OfflineActivityTableActionMenu from './OfflineActivityTableActionMenu';

const HIDDEN_SERIES = [TRAFFIC_TYPES.BOT, TRAFFIC_TYPES.UNRESOLVED];

type Props = {
  data?: OfflineActivityDataType[];
  columns: InstantSearchDataTableColumnDef<OfflineActivityDataType>[];
  isLoading?: boolean;
  error: SolAPIError | null;
  onDisabledSeriesChange: (disabledSeriesType?: TRAFFIC_TYPES[]) => void;
};

const OfflineActivityTable = ({
  data,
  columns,
  isLoading,
  error,
  onDisabledSeriesChange,
}: Props) => {
  const {
    pageFilters: { audience },
  } = usePageFilterContext();

  const [metricSelectorOpen, setMetricSelectorOpen] = useState(false);
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);

  return (
    <Panel
      title={<Trans>Non-Digital Events</Trans>}
      actions={
        <OfflineActivityTableActionMenu
          onOpenMetricSelector={() => setMetricSelectorOpen(true)}
          onOpenAdvancedFilters={() => setAdvancedFiltersOpen(true)}
        />
      }
      infoPopover={{
        title: t`Non-Digital Events`,
        body: (
          <Trans>
            Non-Digital events are activities and campaigns imported from salesforce, more
            information about the setup and customization of these reports can be found{' '}
            <Link variant="body2" color="static-green" to={ROUTES.companySettings.path}>
              {t`HERE`}
            </Link>
          </Trans>
        ),
      }}
      verifyC99Tag
      isFullHeight
    >
      <VisitActivityLegend
        audience={audience}
        hiddenSeries={HIDDEN_SERIES}
        onDisabledSeriesChange={onDisabledSeriesChange}
      />
      <div data-testid="offline-table">
        <InstantSearchDataTable<OfflineActivityDataType>
          columns={columns}
          data={data}
          loading={isLoading}
          error={error}
          emptyMessage={<Trans>No non-digital events found.</Trans>}
          pagination={false}
          getRowId={(record) => record.campaignType.id}
          initialColumnPinning={{
            left: ['vendorId', 'campaignTypeId'],
          }}
        />
      </div>
      <DataTableMetricSelector
        open={metricSelectorOpen}
        onClose={() => setMetricSelectorOpen(false)}
      />
      <DataTableAdvancedFiltersDialog
        open={advancedFiltersOpen}
        onClose={() => setAdvancedFiltersOpen(false)}
      />
    </Panel>
  );
};

export default OfflineActivityTable;
